import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { ParsedParam } from "@/lib/mcp/schema";
import {
  getParameterInputMode,
  getParameterLabel,
  getParameterPlaceholder,
} from "../utils/formatting";

export interface ParameterInputProps {
  parameter: ParsedParam;
  value: string;
  error?: string;
  onChange: (value: string) => void;
  idPrefix?: string;
}

export function ParameterInput({
  parameter,
  value,
  error,
  onChange,
  idPrefix = "param",
}: ParameterInputProps) {
  const inputId = `${idPrefix}-${parameter.name}`;
  const placeholder = getParameterPlaceholder(parameter);
  const inputMode = getParameterInputMode(parameter);
  const label = getParameterLabel(parameter);

  return (
    <div className="space-y-2">
      <Label htmlFor={inputId} className="text-sm font-medium">
        {label}
      </Label>

      {parameter.description && (
        <div className="text-xs text-muted-foreground">
          {parameter.description}
        </div>
      )}

      {/* Render appropriate input based on parameter type */}
      {Array.isArray(parameter.enum) ? (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger id={inputId}>
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {parameter.enum.map((enumValue) => (
              <SelectItem key={String(enumValue)} value={String(enumValue)}>
                {String(enumValue)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : parameter.type === "boolean" ? (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger id={inputId}>
            <SelectValue placeholder="Choose true/false" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">true</SelectItem>
            <SelectItem value="false">false</SelectItem>
          </SelectContent>
        </Select>
      ) : parameter.type === "object" || parameter.type === "array" ? (
        <Textarea
          id={inputId}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="font-mono text-sm"
          rows={4}
        />
      ) : (
        <Input
          id={inputId}
          placeholder={placeholder}
          inputMode={inputMode}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      {error && <div className="text-xs text-destructive">{error}</div>}
    </div>
  );
}
